import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { ProductImportDialog } from '../../components/ProductImportDialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight, 
  Loader2, Package, DollarSign, Image, Download, Link2, ShoppingBag, ExternalLink, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each category becomes a cat-<category> tag in Shopify, which places the product in that collection
const CATEGORY_OPTIONS = ['adults', 'kids', 'masks', 'accessories', 'pets', 'holiday'];

export default function AdminProducts() {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [shopify, setShopify] = useState({ configured: false });
  const [pushingId, setPushingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'adults',
    subcategory: '',
    price: '',
    original_price: '',
    brand: '',
    images: '',
    features: '',
    in_stock: true
  });

  useEffect(() => {
    fetchProducts();
  }, [page, categoryFilter]);

  useEffect(() => {
    axios.get(`${API}/admin/shopify/status`, { headers: getAuthHeader() })
      .then(res => setShopify(res.data))
      .catch(() => {});
  }, []);

  const handleExportCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/export/shopify-csv`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'shopify-products.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded. In Shopify: Products → Import');
    } catch (error) {
      toast.error('Failed to export products');
    }
  };

  const handlePushToShopify = async (product) => {
    setPushingId(product.id);
    try {
      await axios.post(`${API}/admin/products/${product.id}/shopify`, {}, { headers: getAuthHeader() });
      toast.success(product.shopify_product_id ? 'Updated in Shopify' : 'Added to Shopify as a draft');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to Shopify');
    } finally {
      setPushingId(null);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append('search', search);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/admin/products?${params}`, { headers: getAuthHeader() });
      setProducts(response.data.products);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      category: 'adults',
      subcategory: '',
      price: '',
      original_price: '',
      brand: '',
      images: '',
      features: '',
      in_stock: true
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      category: product.category || 'adults',
      subcategory: product.subcategory || '',
      price: product.price?.toString() || '',
      original_price: product.original_price?.toString() || '',
      brand: product.brand || '',
      images: (product.images?.length ? product.images : [product.image_url].filter(Boolean)).join('\n'),
      features: (product.features || []).join('\n'),
      in_stock: product.in_stock !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.price) {
      toast.error('Title and price are required');
      return;
    }

    setSaving(true);
    try {
      const images = formData.images.split('\n').map(i => i.trim()).filter(Boolean);
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        brand: formData.brand,
        image_url: images[0] || '',
        images,
        features: formData.features.split('\n').filter(f => f.trim()),
        in_stock: formData.in_stock
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, { headers: getAuthHeader() });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: getAuthHeader() });
        toast.success('Product created successfully');
      }
      
      setShowModal(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.title}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/products/${product.id}`, { headers: getAuthHeader() });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-products-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Products
            </h2>
            <p className="text-slate-500 mt-1">{total} products in catalog</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExportCsv} data-testid="export-shopify-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Shopify CSV
            </Button>
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-product-btn">
              <Link2 className="w-4 h-4 mr-2" />
              Import from URL
            </Button>
            <Button onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-products"
              />
            </div>
            <Button type="submit" variant="outline">Search</Button>
          </form>
          
          <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
            <SelectTrigger className="w-48" data-testid="filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORY_OPTIONS.map(cat => (
                <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No products found</p>
              <Button onClick={openCreateModal} className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Product
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-600">Product</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Category</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Price</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Stock</th>
                    <th className="text-right p-4 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50" data-testid={`product-row-${product.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.title}
                              className="w-12 h-12 rounded-lg object-cover bg-slate-100"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                              <Package className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900 line-clamp-1">{product.title}</p>
                            <p className="text-sm text-slate-500">{product.brand || 'No brand'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="capitalize">{product.category}</Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{product.price?.toFixed(2)}</span>
                          {product.original_price && product.original_price > product.price && (
                            <span className="text-sm text-slate-400 line-through ml-1">
                              ${product.original_price?.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={product.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {product.in_stock ? 'In Stock' : 'Out of Stock'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {product.shopify_admin_url && (
                            <Button variant="outline" size="sm" asChild title="Open in Shopify">
                              <a href={product.shopify_admin_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </Button>
                          )}
                          {shopify.configured && (
                            <Button
                              variant="outline"
                              size="sm"
                              title={product.shopify_product_id ? 'Update in Shopify' : 'Add to Shopify'}
                              disabled={pushingId === product.id}
                              onClick={() => handlePushToShopify(product)}
                              data-testid={`shopify-${product.id}`}
                            >
                              {pushingId === product.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : product.shopify_product_id
                                  ? <RefreshCw className="w-4 h-4" />
                                  : <ShoppingBag className="w-4 h-4" />}
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openEditModal(product)}
                            data-testid={`edit-${product.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(product)}
                            data-testid={`delete-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <ProductImportDialog
          open={showImport}
          onOpenChange={setShowImport}
          categories={CATEGORY_OPTIONS}
          getAuthHeader={getAuthHeader}
          shopifyConfigured={shopify.configured}
          onImported={() => { setPage(1); fetchProducts(); }}
        />

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Product title"
                    data-testid="product-title"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Product description"
                    rows={3}
                    data-testid="product-description"
                  />
                </div>
                
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger data-testid="product-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <Input
                    id="subcategory"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                    placeholder="e.g., Headphones"
                  />
                </div>
                
                <div>
                  <Label htmlFor="price">Price *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      placeholder="0.00"
                      className="pl-9"
                      data-testid="product-price"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="original_price">Original Price</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="original_price"
                      type="number"
                      step="0.01"
                      value={formData.original_price}
                      onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                      placeholder="0.00"
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    placeholder="Brand name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="in_stock">Stock Status</Label>
                  <Select 
                    value={formData.in_stock ? 'true' : 'false'} 
                    onValueChange={(v) => setFormData({...formData, in_stock: v === 'true'})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">In Stock</SelectItem>
                      <SelectItem value="false">Out of Stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {editingProduct?.variants?.length > 0 && (
                  <p className="col-span-2 text-sm text-slate-500">
                    This product has {editingProduct.variants.length} variants
                    ({editingProduct.options.map(o => o.name).join(' / ')}). Changing the price scales all variant prices.
                  </p>
                )}

                <div className="col-span-2">
                  <Label htmlFor="images">Image URLs (one per line, first is the main image)</Label>
                  <div className="relative">
                    <Image className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      id="images"
                      value={formData.images}
                      onChange={(e) => setFormData({...formData, images: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      rows={3}
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="features">Features (one per line)</Label>
                  <Textarea
                    id="features"
                    value={formData.features}
                    onChange={(e) => setFormData({...formData, features: e.target.value})}
                    placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                    rows={3}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-product-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  editingProduct ? 'Update Product' : 'Create Product'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
